import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InlineBanner,
  InlineBannerLayoutTypesEnum,
  InlineBannerTypesEnum,
} from '../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TimezoneDropdownBox} from '../../../../libs/shared/ui/src/components/datetime/timezoneDropdownBox';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import emptyStateImgSrc from '../../../assets/images/noResults.png';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsWidgetsPageWidgetModel} from '../../../models/api/analyticsWidgetsPageModel';
import {getFirstKnowledgeBaseWithAccess} from '../../../store/data/knowledgeBases/knowledgeBasesSelectors';
import {setIsSidePanelOpenAction} from '../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {SupportReportConfigurationMetric} from './supportReportConfigurationMetric';

/*
 * Props.
 */

interface SupportReportConfigurationFormProps {
  reportName: string;
  timezone: string;
  widgets: ReadonlyArray<AnalyticsWidgetsPageWidgetModel>;
  onTimezoneSelect: (timezone: string) => void;
  onReportNameChange: (reportName: string) => void;
  moveWidget: (fromIndex: number, toIndex: number) => void;
  onDeleteWidget: (index: number) => void;
  onEditWidget: (index: number) => void;
}

/*
 * Style.
 */

const StyledFormContainerDiv = styled.div`
  flex: 1;
`;

const StyledHeadingWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledH2 = styled.h2`
  flex: 1;
`;

const StyledInputWrapperDiv = styled.div`
  max-width: 400px;
`;

const StyledTextBox = styled(TextBox)`
  max-width: 400px;
`;

const StyledMetricWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const StyledEmptyStateWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
`;

const StyledEmptyStateImg = styled.img`
  width: 100px;
  margin-bottom: 16px;
`;

const StyledBottomSpacerDiv = styled.div`
  height: 16px;
`;

const StyledEmptyStateTitleDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
`;

const StyledEmptyStateDescriptionDiv = styled.div`
  line-height: 21px;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledInfoBanner = styled(InlineBanner)`
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const SupportReportConfigurationForm: FC<SupportReportConfigurationFormProps> = ({
  reportName,
  timezone,
  widgets,
  onTimezoneSelect,
  onReportNameChange,
  moveWidget,
  onDeleteWidget,
  onEditWidget,
}) => {
  const knowledgeBase = useAppSelector(getFirstKnowledgeBaseWithAccess);
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  return (
    <StyledFormContainerDiv>
      <StyledInfoBanner
        type={InlineBannerTypesEnum.INFO}
        layoutType={InlineBannerLayoutTypesEnum.MULTILINE}
        actions={
          knowledgeBase
            ? [
                {
                  id: 'analytics-automation-reports-support-report-info-banner-action',
                  title: (
                    <Trans id="analytics-automation-reports-support-report-info-banner-open-analytics">
                      View in knowledge base
                    </Trans>
                  ),
                  onClick: () =>
                    navigateToLink(`/knowledge-base/${knowledgeBase.id}/settings/support-report`),
                },
              ]
            : undefined
        }
      >
        <Trans id="analytics-automation-reports-support-report-info-banner">
          Support Reports are enabled and published in Knowledge Base
        </Trans>
      </StyledInfoBanner>
      <StyledTextBox
        isRequired
        value={reportName}
        onChange={onReportNameChange}
        label={<Trans id="analytics-automation-reports-support-report-name-input">Report name</Trans>}
        placeholder={localize(
          defineMessage({
            id: 'analytics-automation-reports-support-report-name-input-placeholder',
            message: 'Add report name',
          }),
        )}
      />
      <StyledInputWrapperDiv>
        <TimezoneDropdownBox
          showUtc
          onTimezoneSelect={onTimezoneSelect}
          timezone={timezone}
          label={<Trans id="analytics-automation-reports-support-report-timezone">Report timezone</Trans>}
        />
      </StyledInputWrapperDiv>
      <StyledHeadingWrapperDiv>
        <StyledH2>
          <Trans id="analytics-automation-reports-support-report-metrics-header">Metrics</Trans>
        </StyledH2>
        {widgets.length > 0 && (
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => dispatch(setIsSidePanelOpenAction(true))}
          >
            <Trans id="analytics-automation-reports-support-report-add-metric-button">Add metric</Trans>
          </Button>
        )}
      </StyledHeadingWrapperDiv>
      <StyledMetricWrapperDiv>
        {widgets.map((widget, index) => (
          <SupportReportConfigurationMetric
            // eslint-disable-next-line react/no-array-index-key
            key={`${widget.title.replace(' ', '_')}_${index}`}
            widget={widget}
            index={index}
            moveWidget={moveWidget}
            onDelete={() => onDeleteWidget(index)}
            onEdit={() => onEditWidget(index)}
          />
        ))}
        {widgets.length === 0 && (
          <StyledEmptyStateWrapperDiv>
            <StyledEmptyStateImg src={emptyStateImgSrc} alt="Empty state" />
            <StyledEmptyStateTitleDiv>
              <Trans id="analytics-automation-reports-support-report-empty-state-title">No metrics yet</Trans>
            </StyledEmptyStateTitleDiv>
            <StyledEmptyStateDescriptionDiv>
              <Trans id="analytics-automation-reports-support-report-empty-state">
                Add metrics to your report to track and share performance insights
              </Trans>
            </StyledEmptyStateDescriptionDiv>
            <Button
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={() => dispatch(setIsSidePanelOpenAction(true))}
            >
              <Trans id="analytics-automation-reports-support-report-add-metric-button">Add metric</Trans>
            </Button>
          </StyledEmptyStateWrapperDiv>
        )}
      </StyledMetricWrapperDiv>
      <StyledBottomSpacerDiv />
    </StyledFormContainerDiv>
  );
};
